import xlsx, { ISettings, IJsonSheet, IColumn, IContent } from "json-as-xlsx";
import moment from "moment";
import {
  RelatorioUnidadesItem,
  RelatorioUnidadesFields,
  RelatorioUnidadePessoa,
  RelatorioUnidadePdfItem,
} from "features/relatorios/typings";
import { formatCNPJ } from "@brazilian-utils/brazilian-utils";
import { formatCPF } from "@brazilian-utils/brazilian-utils";
import { TipoContatoEnum } from "core/features/auth/typings/user";
import { formatPhone } from "infra/helpers/number";
import { TipoUnidadePessoaEnum } from "core/features/unidades/typings/unidade";
import { sanitizeSheetName } from "infra/helpers/sanitizeSheetName";

export function formatItemsForPdf(items?: RelatorioUnidadesItem[]) {
  const formatted: RelatorioUnidadePdfItem[] = [];

  items?.forEach((item) => {
    item.pessoas.forEach((pessoa) => {
      formatted.push({
        fracao: item.fracao,
        area: item.area,
        areaConstruida: item.areaConstruida,
        peso: item.peso,
        unidade: item.unidade,
        unidadeId: item.unidadeId,
        pessoa,
        observacao: item.observacao,
      });
    });
  });

  return formatted;
}

export function exportExcelRelatorioUnidades({
  items,
  includedFields,
}: {
  items: RelatorioUnidadesItem[];
  includedFields: RelatorioUnidadesFields[];
}) {
  const unidadesSheetItems = formatItemsForPdf(items).map((item) => ({
    ...item,
    ...item.pessoa,
  }));

  const settings: ISettings = {
    fileName: `Relatório de Unidades - ${moment().format("DD_MM_YYYY")}`,
    extraLength: 2,
    writeOptions: {},
  };

  const columns: IColumn[] = [];

  const columnsUnidades: IColumn[] = [];

  columnsUnidades.push({
    label: "Unidade",
    value: (row: IContent) =>
      `Unidade ${(row as unknown as RelatorioUnidadePdfItem).unidade}`,
  });

  if (includedFields.includes("fracao")) {
    columnsUnidades.push({
      label: "Fração",
      value: (row: IContent) =>
        (row as unknown as RelatorioUnidadePdfItem).fracao,
    });
  }

  if (includedFields.includes("peso")) {
    columnsUnidades.push({
      label: "Peso",
      value: (row: IContent) =>
        (row as unknown as RelatorioUnidadePdfItem).peso,
    });
  }

  if (includedFields.includes("area")) {
    columnsUnidades.push({
      label: "Área privativa (m2)",
      value: (row: IContent) =>
        (row as unknown as RelatorioUnidadePdfItem).area,
    });
  }

  if (includedFields.includes("areaConstruida")) {
    columnsUnidades.push({
      label: "Área constrúida (m2)",
      value: (row: IContent) =>
        (row as unknown as RelatorioUnidadePdfItem).areaConstruida ?? "",
    });
  }

  if (includedFields.includes("observacao")) {
    columnsUnidades.push({
      label: "Observação",
      value: (row: IContent) =>
        (row as unknown as RelatorioUnidadePdfItem).observacao ?? "",
    });
  }

  columns.push({
    label: "Nome",
    value: (row: IContent) => (row as unknown as RelatorioUnidadePessoa).nome,
  });

  columnsUnidades.push({
    label: "Nome",
    value: (row: IContent) => (row as unknown as RelatorioUnidadePessoa).nome,
  });

  if (includedFields.includes("documento")) {
    const column = {
      label: "CPF/CNPJ",
      value: (row: IContent) =>
        ((row as unknown as RelatorioUnidadePessoa)?.documento?.length || 0) >
        11
          ? formatCNPJ((row as unknown as RelatorioUnidadePessoa).documento)
          : formatCPF((row as unknown as RelatorioUnidadePessoa).documento),
    };

    columns.push(column);
    columnsUnidades.push(column);
  }

  if (includedFields.includes("contatos")) {
    const _columns = [
      {
        label: "Telefone/Celular",
        value: (row: IContent) =>
          (row as unknown as RelatorioUnidadePessoa).contatos
            .filter((contato) =>
              [TipoContatoEnum.Celular, TipoContatoEnum.Telefone].includes(
                contato.tipoContatoId
              )
            )
            .map((contato) => formatPhone(contato.conteudo))
            .join("; "),
      },
      {
        label: "Email",
        value: (row: IContent) =>
          (row as unknown as RelatorioUnidadePessoa).contatos
            .filter((contato) =>
              [TipoContatoEnum.Email].includes(contato.tipoContatoId)
            )
            .map((contato) => contato.conteudo)
            .join("; "),
      },
    ];

    columns.push(..._columns);
    columnsUnidades.push(..._columns);
  }

  if (includedFields.includes("tipoUnidadePessoaId")) {
    const column = {
      label: "Tipo",
      value: (row: IContent) =>
        (row as unknown as RelatorioUnidadePessoa).tipoUnidadePessoaNome,
    };

    columns.push(column);
    columnsUnidades.push(column);
  }

  if (includedFields.includes("endereco")) {
    const column = {
      label: "Endereço",
      value: (row: IContent) =>
        (row as unknown as RelatorioUnidadePessoa).endereco,
    };

    columns.push(column);
    columnsUnidades.push(column);
  }

  const contentMapper = (item: Record<string, any>) => {
    const obj: Record<string, any> = {};

    Object.keys(item).forEach((key) => {
      obj[key] = item[key] ?? "";
    });

    return obj;
  };

  // Fazemos isso pois o nome da página (sheet) no excel deve ser único, caso exista uma unidade com número repetido , acrescenta-se um índice na frente [(2), (3)...]
  const unidadesJaIncluidas = new Map<string, number>();

  const sheetsUnidades: IJsonSheet[] = items.map((item) => {
    const sheetNameOriginal = sanitizeSheetName(item.unidade.slice(0, 31));

    let sheetName = sheetNameOriginal;
    if (unidadesJaIncluidas.has(sheetNameOriginal)) {
      const valorAtual = unidadesJaIncluidas.get(sheetNameOriginal)!;
      sheetName = `${sheetName} (${valorAtual + 1})`.slice(0, 31);
      unidadesJaIncluidas.set(sheetNameOriginal, valorAtual + 1);
    } else {
      unidadesJaIncluidas.set(sheetNameOriginal, 1);
    }

    return {
      sheet: sheetName,
      columns: columns,
      content: item.pessoas.map(contentMapper),
    };
  });

  const data: IJsonSheet[] = [
    {
      sheet: "Unidades",
      columns: columnsUnidades,
      content: unidadesSheetItems.map(contentMapper),
    },
    ...sheetsUnidades,
  ];

  xlsx(data, settings);
}

export const fieldsModal: {
  label: string;
  value: RelatorioUnidadesFields;
}[] = [
  {
    label: "CPF/CNPJ",
    value: "documento",
  },
  {
    label: "Contatos",
    value: "contatos",
  },
  {
    label: "Tipo",
    value: "tipoUnidadePessoaId",
  },
  {
    label: "Endereço",
    value: "endereco",
  },
  {
    label: "Fração",
    value: "fracao",
  },
  {
    label: "Peso",
    value: "peso",
  },
  {
    label: "Área privativa",
    value: "area",
  },
  {
    label: "Área construída",
    value: "areaConstruida",
  },
  {
    label: "Observação",
    value: "observacao",
  },
];

export const extraFieldsModal: {
  label: string;
  value: string;
}[] = [
  {
    label: "Proprietário",
    value: TipoUnidadePessoaEnum.Proprietario,
  },
  {
    label: "Coproprietário",
    value: TipoUnidadePessoaEnum.CoProprietario,
  },
  {
    label: "Sócio",
    value: TipoUnidadePessoaEnum.Socio,
  },
  {
    label: "Locatário",
    value: TipoUnidadePessoaEnum.Locatario,
  },
  {
    label: "Outros",
    value: TipoUnidadePessoaEnum.Outros,
  },
];
